import type { MouseEvent } from "react";
import { Bell, CreditCard, FileSearch, ShieldAlert } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { AppLink } from "@/lib/routing";
import type { InboxNotification } from "@/lib/autar/notifications";
import {
  categoryKey,
  renderNotification,
} from "@/features/notifications/renderers";
import { formatRelativeTime } from "@/features/notifications/relative-time";
import "@/components/notification-item.css";

const ICONS = {
  security_alerts: ShieldAlert,
  scan_results: FileSearch,
  billing: CreditCard,
} as const;

interface NotificationItemProps {
  item: InboxNotification;
  /** Runs on a click, before the link navigates. */
  onOpen?: (
    item: InboxNotification,
    event: MouseEvent<HTMLAnchorElement>,
  ) => void;
}

/** The one row every notification type renders through, in the bell and on the page. */
export function NotificationItem({ item, onOpen }: NotificationItemProps) {
  const { t, locale } = useI18n();
  const view = renderNotification(item, t, locale);
  const Icon = ICONS[item.category as keyof typeof ICONS] ?? Bell;
  const unread = !item.readAt;
  return (
    <AppLink
      to={view.href}
      className={`notification-item${unread ? " is-unread" : ""}`}
      onClick={(event) => onOpen?.(item, event)}
    >
      <span className="notification-item-icon" aria-hidden="true">
        <Icon size={16} />
      </span>
      <span className="notification-item-text">
        <strong>{view.title}</strong>
        {view.body && <span>{view.body}</span>}
        <small className="notification-item-meta">
          <span className="notification-item-chip">
            {t(categoryKey(item.category))}
          </span>
          {formatRelativeTime(item.createdAt, locale)}
        </small>
      </span>
      <span
        className={`notification-bell-dot${unread ? "" : " is-read"}`}
        role={unread ? "img" : undefined}
        aria-label={unread ? t("notifications.unread") : undefined}
        aria-hidden={unread ? undefined : true}
      />
    </AppLink>
  );
}
